import { useMemo } from 'react';
import { METAL_IDS } from '../constants';
import { 
  calculateMovementCents, 
  calculateProgressiveSoldes, 
  filterMovements,
  prepareTabData 
} from '../utils';

/**
 * Hook principal pour calculer et préparer les données des mouvements
 */
export const useMouvementsData = (rawData, currentTab, filters) => {
  // Préparer les données pour l'onglet actuel
  const currentTabData = useMemo(() => {
    return prepareTabData(rawData, currentTab);
  }, [rawData, currentTab]);

  // Toutes les données pour le calcul des soldes
  const allCompleteData = useMemo(() => {
    const fournisseursAll = rawData.fournisseursRows.filter(r => 
      (parseFloat(r.entree_or_fin != null ? r.entree_or_fin : r.entree_fin) || 0) > 0
    );
    
    const clientsAll = rawData.clientsRows.filter(r => {
      const entree = parseFloat((r.entree_or_fin != null ? r.entree_or_fin : r.entree_fin) || 0) || 0;
      const sortie = parseFloat((r.sortie_or_fin != null ? r.sortie_or_fin : r.sortie_fin) || 0) || 0;
      return entree > 0 || sortie > 0;
    });
    
    return [...rawData.clalNorm, ...fournisseursAll, ...clientsAll];
  }, [rawData]);

  // Calcul des données progressives par métal
  const progressiveByMetal = useMemo(() => {
    // 1. Enrichir toutes les données avec les calculs
    let allDataForSoldes = [];
    
    if (currentTab === 1) {
      allDataForSoldes = rawData.fournisseursRows.map(row => {
        const isGold = String(row.metal_id || '1') === METAL_IDS.OR;
        const { entreeFinCents, sortieFinCents, perteFinCents } = calculateMovementCents(row, isGold);
        return { ...row, __entreeFinCents: entreeFinCents, __sortieFinCents: sortieFinCents, __perteFinCents: perteFinCents };
      });
    } else if (currentTab === 2) {
      allDataForSoldes = rawData.clientsRows.map(row => {
        const { entreeFinCents, sortieFinCents, perteFinCents } = calculateMovementCents(row, false);
        return { ...row, __entreeFinCents: entreeFinCents, __sortieFinCents: sortieFinCents, __perteFinCents: perteFinCents };
      });
    } else {
      allDataForSoldes = allCompleteData.map(row => {
        const isGold = String(row.metal_id || '1') === METAL_IDS.OR;
        const { entreeFinCents, sortieFinCents, perteFinCents } = calculateMovementCents(row, isGold, true);
        return { ...row, __entreeFinCents: entreeFinCents, __sortieFinCents: sortieFinCents, __perteFinCents: perteFinCents };
      });
    }

    // 2. Calculer les soldes progressifs
    calculateProgressiveSoldes(allDataForSoldes);

    // 3. Filtrer pour l'affichage
    const filteredData = filterMovements(currentTabData, filters);

    // 4. Enrichir et grouper par métal
    const displayGroups = {};
    filteredData.forEach(row => {
      const completeRow = allDataForSoldes.find(cr => 
        cr.id === row.id && cr.__source === row.__source
      );
      
      let enrichedRow = {
        ...row,
        __entreeFinCents: completeRow?.__entreeFinCents || 0,
        __sortieFinCents: completeRow?.__sortieFinCents || 0,
        __perteFinCents: completeRow?.__perteFinCents || 0,
        __soldeMetalCents: completeRow?.__soldeMetalCents || 0
      };

      // Adapter les montants pour l'affichage selon la vue
      if (currentTab !== 0 && completeRow) {
        const isGold = String(row.metal_id || '1') === METAL_IDS.OR;
        const { entreeFinCents, sortieFinCents, perteFinCents } = calculateMovementCents(row, isGold);
        enrichedRow = {
          ...enrichedRow,
          __entreeFinCents: entreeFinCents,
          __sortieFinCents: sortieFinCents,
          __perteFinCents: perteFinCents
        };
      }
      
      const metalId = String(row.metal_id || '1');
      if (!displayGroups[metalId]) displayGroups[metalId] = [];
      displayGroups[metalId].push(enrichedRow);
    });

    // 5. Trier chaque groupe par date (plus récent en premier)
    Object.keys(displayGroups).forEach(metalId => {
      displayGroups[metalId].sort((a, b) => {
        if (a.date_mouvement === b.date_mouvement) return (b.id || 0) - (a.id || 0);
        return b.date_mouvement.localeCompare(a.date_mouvement);
      });
    });
    
    return { displayGroups, totalFilteredCount: filteredData.length };
  }, [allCompleteData, currentTabData, currentTab, filters, rawData]);

  return progressiveByMetal;
};
